import React from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  Button,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Divider,
  Heading,
  Flex,
  IconButton,
  Tooltip,
  Skeleton
} from "@chakra-ui/react";
import { 
  FiDollarSign, 
  FiUsers, 
  FiClock, 
  FiCheckCircle, 
  FiAlertTriangle,
  FiRefreshCw,
  FiPlus,
  FiEye,
  FiTrendingUp,
  FiTrendingDown
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { 
  useGetPayrollDashboard, 
  useGetCompanyWallet,
  useGetPendingHRApprovals,
  useGetPendingAccountantApprovals
} from "../../../services/payrollService";

const PayrollDashboard = () => {
  const { user } = useUserContext();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // Comprehensive API data from single endpoint
  const { data: dashboardResponse, isLoading: dashboardLoading, error: dashboardError } = useGetPayrollDashboard();
  
  // Extract data from the comprehensive response
  const dashboardData = dashboardResponse?.data;
  const financialSummary = dashboardData?.financial_summary;
  const payrollReadiness = dashboardData?.payroll_readiness;
  const statistics = dashboardData?.statistics;
  const recentBatches = dashboardData?.recent_batches;
  const hrApprovals = dashboardData?.hr_approvals;
  const accountantApprovals = dashboardData?.accountant_approvals;
  const monthlyTrends = dashboardData?.monthly_trends;
  const currentPeriod = dashboardData?.current_period;
  const totalCount = statistics?.total_batches || 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'pending_hr_approval': return 'orange';
      case 'pending_accountant_approval': return 'blue';
      case 'processing': return 'purple';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'pending_hr_approval': return 'Pending HR Approval';
      case 'pending_accountant_approval': return 'Pending Accountant Approval';
      case 'processing': return 'Processing';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: "$",
      NGN: "₦",
      GHS: "₵",
      KES: "KSh",
      ZAR: "R"
    };
    return symbols[currency] || currency;
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Summary Statistics */}
      {dashboardLoading ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
              <Skeleton height="20px" mb={2} />
              <Skeleton height="30px" mb={2} />
              <Skeleton height="16px" />
            </Box>
          ))}
        </SimpleGrid>
      ) : dashboardError ? (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertTitle>Failed to load dashboard data</AlertTitle>
          <AlertDescription>
            {dashboardError.message || "Unable to retrieve dashboard information"}
          </AlertDescription>
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
            <StatLabel>Total Batches</StatLabel>
            <StatNumber color="blue.500">{statistics?.total_batches || 0}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              Active batches
            </StatHelpText>
          </Stat>

          <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
            <StatLabel>Pending HR Approvals</StatLabel>
            <StatNumber color="orange.500">{statistics?.pending_hr_approvals || 0}</StatNumber>
            <StatHelpText>
              <StatArrow type="decrease" />
              Awaiting review
            </StatHelpText>
          </Stat>

          <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
            <StatLabel>Pending Accountant Approvals</StatLabel>
            <StatNumber color="blue.500">{statistics?.pending_accountant_approvals || 0}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              Financial review
            </StatHelpText>
          </Stat>

          <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
            <StatLabel>Total Employees</StatLabel>
            <StatNumber color="green.500">{statistics?.total_employees || 0}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              Active employees
            </StatHelpText>
          </Stat>
        </SimpleGrid>
      )}

      {/* Financial Summary */}
      {dashboardLoading ? (
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <Skeleton height="24px" width="150px" />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {[1, 2, 3].map((i) => (
                <Box key={i} p={4} bg="gray.50" borderRadius="md">
                  <Skeleton height="16px" mb={2} />
                  <Skeleton height="20px" />
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Box>
      ) : financialSummary ? (
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Heading size="md" color="gray.800">
                Financial Summary
              </Heading>
              <Badge 
                colorScheme={financialSummary.funding_status === 'sufficient' ? 'green' : 'red'}
                variant="subtle"
              >
                {financialSummary.funding_status === 'sufficient' ? 'Sufficient Funds' : 'Insufficient Funds'}
              </Badge>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Wallet Balance
                </Text>
                <Text fontSize="lg" fontWeight="600" color="gray.800">
                  {getCurrencySymbol(financialSummary.currency)}{financialSummary.wallet_balance?.toLocaleString()}
                </Text>
              </Box>
              
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Monthly Requirement (Regular Pay)
                </Text>
                <Text fontSize="lg" fontWeight="600" color="gray.800">
                  {getCurrencySymbol(financialSummary.currency)}{financialSummary.funding_requirements?.regular_pay_monthly?.toLocaleString()}
                </Text>
              </Box>
            
            <Box p={4} bg="gray.50" borderRadius="md">
              <Text fontSize="sm" color="gray.600" mb={1}>
                  Monthly Requirement (Total Pay)
              </Text>
              <Text fontSize="lg" fontWeight="600" color="gray.800">
                  {getCurrencySymbol(financialSummary.currency)}{financialSummary.funding_requirements?.total_pay_monthly?.toLocaleString()}
              </Text>
            </Box>
            </SimpleGrid>
            
            {financialSummary.recommended_action && (
              <Alert 
                status={financialSummary.funding_status === 'sufficient' ? 'success' : 'warning'} 
                borderRadius="md"
              >
                <AlertIcon />
                <Box>
                  <AlertTitle>
                    {financialSummary.funding_status === 'sufficient' ? 'Ready for Payroll' : 'Action Required'}
                  </AlertTitle>
                  <AlertDescription>
                    {financialSummary.recommended_action === 'fund_wallet' 
                      ? 'Insufficient funds for payroll. Please fund your wallet.'
                      : 'Your wallet has sufficient funds for payroll processing.'
                    }
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </VStack>
        </Box>
      ) : null}

      {/* Payroll Readiness */}
      {payrollReadiness && (
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Heading size="md" color="gray.800">
                Payroll Readiness
              </Heading>
              <Badge 
                colorScheme={payrollReadiness.ready_for_payroll ? 'green' : 'orange'}
                variant="subtle"
              >
                {payrollReadiness.ready_for_payroll ? 'Ready' : 'Not Ready'}
              </Badge>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Total Employees
                </Text>
                <Text fontSize="lg" fontWeight="600" color="gray.800">
                  {payrollReadiness.total_employees}
                </Text>
              </Box>
              
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  With Bank Details
                </Text>
                <Text fontSize="lg" fontWeight="600" color="green.600">
                  {payrollReadiness.employees_with_bank_details}
                </Text>
              </Box>
              
              <Box p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Without Bank Details
                </Text>
                <Text fontSize="lg" fontWeight="600" color="red.600">
                  {payrollReadiness.employees_without_bank_details}
                </Text>
              </Box>
            </SimpleGrid>
            
            <Box p={4} bg="gray.50" borderRadius="md">
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.600">
                  Readiness Percentage
                </Text>
                <Text fontSize="sm" fontWeight="600" color="gray.800">
                  {payrollReadiness.readiness_percentage?.toFixed(1)}%
                </Text>
              </HStack>
              <Box w="100%" bg="gray.200" borderRadius="full" h="8px">
                <Box 
                  w={`${payrollReadiness.readiness_percentage}%`} 
                  bg={payrollReadiness.readiness_percentage >= 80 ? 'green.500' : payrollReadiness.readiness_percentage >= 50 ? 'orange.500' : 'red.500'}
                  borderRadius="full" 
                  h="8px"
                  transition="width 0.3s ease"
                />
              </Box>
            </Box>
            
            {!payrollReadiness.ready_for_payroll && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Action Required</AlertTitle>
                  <AlertDescription>
                    {payrollReadiness.employees_without_bank_details} employees need to provide bank details before payroll can be processed.
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </VStack>
        </Box>
      )}

      {/* Smart Quick Actions */}
      <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <Heading size="md" color="gray.800">
            Quick Actions
          </Heading>
          
          {/* Primary Actions - High Priority */}
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="wide">
              Priority Actions
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {/* Fund Wallet - Urgent */}
            <Button
                leftIcon={<FiDollarSign />}
                colorScheme="red"
                variant="solid"
              size="md"
              borderRadius="lg"
                position="relative"
                onClick={() => {
                  // Navigate to Wallet Management tab
                  const event = new CustomEvent('navigateToTab', { detail: 'wallet-management' });
                  window.dispatchEvent(event);
                }}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                transition="all 0.2s"
              >
                <VStack spacing={1} align="center">
                  <Text fontWeight="600">Fund Wallet</Text>
                  <Text fontSize="xs" opacity={0.9}>
                    {financialSummary?.funding_status === 'insufficient' ? 'Insufficient Funds' : 'Wallet Balance'}
                  </Text>
                </VStack>
                {financialSummary?.funding_status === 'insufficient' && (
                  <Badge
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    colorScheme="red"
                    borderRadius="full"
                    fontSize="xs"
                  >
                    !
                  </Badge>
                )}
            </Button>
            
              {/* Employee Bank Details - High Priority */}
            <Button
                leftIcon={<FiUsers />}
                colorScheme="orange"
                variant="solid"
              size="md"
              borderRadius="lg"
                position="relative"
                onClick={() => {
                  // Navigate to Employee Bank Details tab
                  const event = new CustomEvent('navigateToTab', { detail: 'employee-bank-details' });
                  window.dispatchEvent(event);
                }}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                transition="all 0.2s"
              >
                <VStack spacing={1} align="center">
                  <Text fontWeight="600">Bank Details</Text>
                  <Text fontSize="xs" opacity={0.9}>
                    {payrollReadiness?.employees_without_bank_details || 0} missing
                  </Text>
                </VStack>
                {payrollReadiness?.employees_without_bank_details > 0 && (
                  <Badge
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    colorScheme="orange"
                    borderRadius="full"
                    fontSize="xs"
                  >
                    {payrollReadiness.employees_without_bank_details}
                  </Badge>
                )}
            </Button>
            
              {/* HR Approvals - Medium Priority */}
            <Button
              leftIcon={<FiCheckCircle />}
              colorScheme="orange"
              variant="outline"
              size="md"
              borderRadius="lg"
                position="relative"
                onClick={() => {
                  // Navigate to HR Approvals tab
                  const event = new CustomEvent('navigateToTab', { detail: 'hr-approvals' });
                  window.dispatchEvent(event);
                }}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                transition="all 0.2s"
              >
                <VStack spacing={1} align="center">
                  <Text fontWeight="600">HR Approvals</Text>
                  <Text fontSize="xs" opacity={0.9}>
                    {statistics?.pending_hr_approvals || 0} pending
                  </Text>
                </VStack>
                {statistics?.pending_hr_approvals > 0 && (
                  <Badge
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    colorScheme="orange"
                    borderRadius="full"
                    fontSize="xs"
                  >
                    {statistics.pending_hr_approvals}
                  </Badge>
                )}
              </Button>
            </SimpleGrid>
          </VStack>

          {/* Secondary Actions - Standard */}
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" fontWeight="600" color="gray.600" textTransform="uppercase" letterSpacing="wide">
              Standard Actions
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {/* Create Batch */}
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                variant="outline"
                size="md"
                borderRadius="lg"
                onClick={() => {
                  // Navigate to Batch Management tab
                  const event = new CustomEvent('navigateToTab', { detail: 'batch-management' });
                  window.dispatchEvent(event);
                }}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                transition="all 0.2s"
              >
                <VStack spacing={1} align="center">
                  <Text fontWeight="600">Create Batch</Text>
                  <Text fontSize="xs" opacity={0.9}>
                    New payroll batch
                  </Text>
                </VStack>
            </Button>
            
              {/* Track Payments */}
            <Button
              leftIcon={<FiClock />}
              colorScheme="purple"
              variant="outline"
              size="md"
              borderRadius="lg"
                position="relative"
                onClick={() => {
                  // Navigate to Payment Tracking tab
                  const event = new CustomEvent('navigateToTab', { detail: 'payment-tracking' });
                  window.dispatchEvent(event);
                }}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                transition="all 0.2s"
              >
                <VStack spacing={1} align="center">
                  <Text fontWeight="600">Track Payments</Text>
                  <Text fontSize="xs" opacity={0.9}>
                    {totalCount || 0} batches
                  </Text>
                </VStack>
                {totalCount > 0 && (
                  <Badge
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    colorScheme="purple"
                    borderRadius="full"
                    fontSize="xs"
                  >
                    {totalCount}
                  </Badge>
                )}
              </Button>

              {/* View Reports */}
              <Button
                leftIcon={<FiEye />}
                colorScheme="green"
                variant="outline"
                size="md"
                borderRadius="lg"
                onClick={() => {
                  // Navigate to Reports section
                  const event = new CustomEvent('navigateToTab', { detail: 'reports' });
                  window.dispatchEvent(event);
                }}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg'
                }}
                transition="all 0.2s"
              >
                <VStack spacing={1} align="center">
                  <Text fontWeight="600">View Reports</Text>
                  <Text fontSize="xs" opacity={0.9}>
                    Analytics & insights
                  </Text>
                </VStack>
            </Button>
          </SimpleGrid>
          </VStack>
        </VStack>
      </Box>

      {/* Recent Batches */}
      <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between">
            <Heading size="md" color="gray.800">
              Recent Batches
            </Heading>
            <Button size="sm" variant="ghost" colorScheme="blue">
              View All
            </Button>
          </HStack>
          
          {dashboardLoading ? (
            <VStack spacing={3} align="stretch">
              {[1, 2].map((i) => (
                <Box key={i} p={4} bg="gray.50" borderRadius="md">
                  <Skeleton height="20px" mb={2} />
                  <Skeleton height="16px" mb={2} />
                  <Skeleton height="14px" />
                </Box>
              ))}
            </VStack>
          ) : recentBatches?.length > 0 ? (
            <VStack spacing={3} align="stretch">
              {recentBatches.map((batch) => (
                <Box key={batch.batch_id} p={4} bg="gray.50" borderRadius="md">
                  <HStack justify="space-between" mb={2}>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="600" color="gray.800">
                        {batch.batch_name}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {batch.employee_count} employees • {getCurrencySymbol(batch.items?.[0]?.currency || 'NGN')}{batch.total_amount?.toLocaleString()}
                      </Text>
                    </VStack>
                    <Badge colorScheme={getStatusColor(batch.status)}>
                      {getStatusText(batch.status)}
                    </Badge>
                  </HStack>
                  
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">
                      Created: {new Date(batch.created_at).toLocaleDateString()}
                    </Text>
                    <Button size="xs" variant="ghost" colorScheme="blue">
                      View Details
                    </Button>
                  </HStack>
                </Box>
              ))}
            </VStack>
          ) : (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <AlertDescription>
                No recent batches found.
              </AlertDescription>
            </Alert>
          )}
        </VStack>
      </Box>

      {/* Alerts */}
      {!dashboardLoading && statistics && (
        <>
          {statistics.pending_hr_approvals > 0 && (
            <Alert status="warning" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Pending HR Approvals!</AlertTitle>
                <AlertDescription>
                  You have {statistics.pending_hr_approvals} payroll batches waiting for HR approval.
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {statistics.pending_accountant_approvals > 0 && (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Pending Accountant Approvals!</AlertTitle>
                <AlertDescription>
                  You have {statistics.pending_accountant_approvals} payroll batches waiting for accountant approval.
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {statistics.rejected_batches > 0 && (
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Rejected Batches!</AlertTitle>
                <AlertDescription>
                  {statistics.rejected_batches} batch(es) have been rejected. Please review and resubmit.
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </>
      )}
    </VStack>
  );
};

export default PayrollDashboard;